import { Injectable } from '@angular/core';

/** Probe key used only to detect whether the real storage is reachable. */
const AVAILABILITY_PROBE_KEY = '__treino_storage_probe__';

/**
 * Wrapper around `localStorage` that degrades gracefully when the browser denies
 * access to site data — Chrome's "block all cookies", a third-party iframe, or an
 * in-app WebView. In those contexts even *reading* the `window.localStorage`
 * property throws `SecurityError` (DOMException code 18), so every access is
 * guarded and falls back to an in-memory map: values stay usable for the rest of
 * the session, they just don't survive a reload.
 */
@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  private readonly actuarPrefixKey = 'treino/';

  /** Stand-in used when the real storage is blocked, and mirror of every write. */
  private readonly memoryStore = new Map<string, string>();

  /** The real storage, or `null` when it is unreachable (blocked / non-browser). */
  private readonly storage: Storage | null = resolveStorage();

  /** True when writes are only kept in memory — persistence is off for this session. */
  public get isPersistent(): boolean {
    return this.storage !== null;
  }

  public setItem(key: string, value: string): void {
    const _key: string = this.actuarPrefixKey + key;
    // Mirrored first so a quota/security failure below can't lose the value.
    this.memoryStore.set(_key, value);
    try {
      this.storage?.setItem(_key, value);
    } catch {
      // Quota exceeded or access revoked mid-session — the in-memory copy stands in.
    }
  }

  public getItem(key: string): string | null {
    const _key: string = this.actuarPrefixKey + key;
    try {
      const value = this.storage?.getItem(_key) ?? null;
      if (value !== null) return value;
    } catch {
      // Access revoked mid-session — fall through to the in-memory copy.
    }
    return this.memoryStore.get(_key) ?? null;
  }

  public removeItem(key: string): void {
    const _key: string = this.actuarPrefixKey + key;
    this.memoryStore.delete(_key);
    try {
      this.storage?.removeItem(_key);
    } catch {
      // Nothing to do: the in-memory copy is already gone.
    }
  }

  public clearAllData(): void {
    // 'remote-config:v1' fica na lista porque não é dado de usuário: é o que
    // permite ao próximo launch bootar sem rede. Apagá-lo no logout empurraria
    // a abertura seguinte para o caminho bloqueante — e prenderia na tela de
    // erro quem saísse da conta estando offline.
    const keysToPreserve = ['Email', 'Alarm', 'consentMode', 'feedbackGiven', 'biometric', 'remote-config:v1'];
    const preservedData: Record<string, string | null> = {};

    keysToPreserve.forEach(key => {
      preservedData[key] = this.getItem(key);
    });

    this.memoryStore.clear();
    try {
      this.storage?.clear();
    } catch {
      // Already cleared in memory; nothing else we can do.
    }

    keysToPreserve.forEach(key => {
      if (preservedData[key] !== null) {
        this.setItem(key, preservedData[key]!);
      }
    });
  }
}

/**
 * Resolves `window.localStorage` once, returning `null` when it is unavailable.
 * Both the property read and a probe call can throw, so both are covered.
 */
function resolveStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    const storage = window.localStorage;
    // Read-only probe: enough to surface a blocked storage, without spending quota.
    storage.getItem(AVAILABILITY_PROBE_KEY);
    return storage;
  } catch {
    return null;
  }
}
